import SearchOff from "@mui/icons-material/SearchOff";
import { Box, Button, LinearProgress, Skeleton, Stack, Typography } from "@mui/material";
import type { ReactNode } from "react";
import type { LoadPhase, PatientsProgress } from "src-next/modules/clinical-insights/loadPhase";

interface InsightWorkspaceResultBodyProps {
  loadPhase: LoadPhase;
  patientsProgress: PatientsProgress;
  accentColor: string;
  isEmpty: boolean;
  emptyPeriodLabel: string;
  emptyPractitionerLabel: string;
  emptyListPrefix: string;
  errorText: string;
  showClearPractitionerAction: boolean;
  onClearPractitioner: () => void;
  onPreviousPeriod: () => void;
  onRetry: () => void;
  initialSkeleton?: boolean;
  children: ReactNode;
}

export function InsightWorkspaceResultBody({
  loadPhase,
  patientsProgress,
  accentColor,
  isEmpty,
  emptyPeriodLabel,
  emptyPractitionerLabel,
  emptyListPrefix,
  errorText,
  showClearPractitionerAction,
  onClearPractitioner,
  onPreviousPeriod,
  onRetry,
  initialSkeleton = false,
  children,
}: InsightWorkspaceResultBodyProps) {
  if (initialSkeleton) {
    return (
      <Stack spacing={1} sx={{ py: 1, flex: 1 }}>
        {Array.from({ length: 8 }).map((_, i) => (
          <Skeleton key={i} variant="rounded" height={52} />
        ))}
      </Stack>
    );
  }

  if (loadPhase === "error") {
    return (
      <Stack alignItems="center" spacing={2} sx={{ py: 4, px: 2, textAlign: "center" }}>
        <Typography variant="body1">{errorText}</Typography>
        <Button variant="outlined" onClick={onRetry} sx={{ borderColor: accentColor, color: accentColor }}>
          Tentar novamente
        </Button>
      </Stack>
    );
  }

  if (loadPhase === "patients") {
    const { loaded, total } = patientsProgress;
    const value = total > 0 ? (loaded / total) * 100 : 0;
    return (
      <Stack spacing={2} sx={{ py: 3, px: 1 }}>
        <Typography variant="body2" color="text.secondary" fontWeight={600}>
          Carregando pacientes {loaded} de {total}
        </Typography>
        <LinearProgress
          variant="determinate"
          value={value}
          sx={{
            height: 6,
            borderRadius: 3,
            bgcolor: `${accentColor}22`,
            "& .MuiLinearProgress-bar": { bgcolor: accentColor },
          }}
        />
      </Stack>
    );
  }

  if (isEmpty && loadPhase === "done") {
    return (
      <Stack alignItems="center" spacing={2} sx={{ py: 4, px: 2, textAlign: "center" }}>
        <SearchOff sx={{ fontSize: 40, color: "text.disabled" }} />
        <Typography variant="body1" color="text.secondary">
          {emptyListPrefix} em {emptyPeriodLabel} para {emptyPractitionerLabel}
        </Typography>
        <Stack direction="row" flexWrap="wrap" justifyContent="center" gap={1}>
          {showClearPractitionerAction ? (
            <Button size="small" variant="outlined" onClick={onClearPractitioner}>
              Ver todos os profissionais
            </Button>
          ) : null}
          <Button size="small" variant="outlined" onClick={onPreviousPeriod}>
            Mês anterior
          </Button>
        </Stack>
      </Stack>
    );
  }

  return <Box sx={{ minHeight: 0, flex: 1, display: "flex", flexDirection: "column" }}>{children}</Box>;
}
